import { filterInvoices } from "@app/analytics/account/Billing/utils";
import { get, httpDelete, patch, post } from "@app/common/api";
import type {
  ApiBillingSubscription,
  ApiInitiateCheckoutResponse,
  ApiPreviewInvoice,
  ApiPromotion,
  ApiSetupIntent,
  BillingCustomer,
  BillingSubscription,
  InitiateCheckoutResponse,
  InitiateCreditCheckoutApiRequestBody,
  InitiateCreditCheckoutRequestBody,
  InitiateSubscriptionCheckoutApiRequestBody,
  InitiateSubscriptionCheckoutRequestBody,
  Invoice,
  PatchSubscriptionRequestBody,
  PaymentMethod,
  PreviewInvoice,
  Promotion,
  SetupIntent,
  UserBillingAccount,
} from "@app/common/types";
import { withQueryParamsUrl } from "@app/common/utils";
import { optionalParseUTCDateTimeFromString } from "@app/utils/utils";

const parsePreviewInvoice = (invoice: ApiPreviewInvoice): PreviewInvoice => ({
  ...invoice,
  periodStart: optionalParseUTCDateTimeFromString(invoice.periodStart),
  periodEnd: optionalParseUTCDateTimeFromString(invoice.periodEnd),
});

const parseInitiateCheckoutResponse = ({
  clientSecret,
  clientSecretType,
  invoice,
}: ApiInitiateCheckoutResponse): InitiateCheckoutResponse => {
  return {
    // The BE will return a null client secret if something goes wrong, and Stripe
    // elements will break on null (but not on undefined), so default to undefined
    clientSecret: clientSecret ?? undefined,
    clientSecretType,
    invoice: parsePreviewInvoice(invoice),
  };
};

export const syncBilling = async (): Promise<void> => {
  return post("/billing/sync", {}, { withCreds: true });
};

export const getCustomer = async (): Promise<BillingCustomer> => {
  // this is an upsert endpoint that will create a billing account/stripe
  // customer id for a user if one does not already exist.
  // The full customer will only be returned if the current user is the
  // billing owner
  return get("/billing/customer", { withCreds: true });
};

export const patchCustomer = (
  patchCustomer: { id: BillingCustomer["id"] } & Partial<BillingCustomer>
): Promise<void> => {
  return patch(
    `/billing/customer/${patchCustomer.id}`,
    { company: patchCustomer.company },
    { withCreds: true }
  );
};

export const createSetupIntent = async (
  billingAccountId: UserBillingAccount["id"],
  promotionId?: string
): Promise<SetupIntent> => {
  const response: ApiSetupIntent = await post(
    withQueryParamsUrl(`/billing/${billingAccountId}/setup-intent`, {
      promotion_id: promotionId,
    }),
    {},
    { withCreds: true }
  );

  // The BE will return a null client secret if something goes wrong, and Stripe
  // elements will break on null (but not on undefined), so default to undefined
  return { clientSecret: response.clientSecret ?? undefined };
};

export const initiateSubscriptionCheckout = async (
  body: InitiateSubscriptionCheckoutRequestBody
): Promise<InitiateCheckoutResponse> => {
  const { billingAccountId, subscription, billingInfo } = body;
  const requestBody: InitiateSubscriptionCheckoutApiRequestBody = {
    cadence: subscription.cadence,
    plan: subscription.plan,
    quantity: subscription.quantity,
    billingDetails: billingInfo,
  };

  const response = await post(
    `/billing/${billingAccountId}/payment-setup`,
    requestBody,
    { withCreds: true }
  );

  return parseInitiateCheckoutResponse(response);
};

export const initiateUploadCreditsCheckout = async (
  body: InitiateCreditCheckoutRequestBody
) => {
  const { billingAccountId } = body;

  const requestBody: InitiateCreditCheckoutApiRequestBody = {
    quantity: body.quantity,
    billingDetails: body.billingInfo,
  };

  const response = await post(
    `/billing/${billingAccountId}/upload-credit-payment-setup`,
    requestBody,
    { withCreds: true }
  );

  return parseInitiateCheckoutResponse(response);
};

export const cancelCreditsCheckout = async (
  billingAccountId: UserBillingAccount["id"]
) => {
  return post(
    `/billing/${billingAccountId}/upload-credit-payment-cancel`,
    {},
    { withCreds: true }
  );
};

export const createPaymentPortalLink = (
  billingAccountId: UserBillingAccount["id"],
  options: { returnUrl: string; flow: "payment" | "address" }
): Promise<{ url: string }> => {
  return post(
    `/billing/${billingAccountId}/payment-method-link`,
    { returnUrl: options.returnUrl, flow: options.flow },
    { withCreds: true }
  );
};

export const getPaymentMethod = async (
  billingAccountId: UserBillingAccount["id"]
): Promise<{ paymentMethod: PaymentMethod }> => {
  return get(`/billing/${billingAccountId}/payment-method`, {
    withCreds: true,
  });
};

export const removePaymentMethod = async (
  billingAccountId: UserBillingAccount["id"]
) => {
  return httpDelete(`/billing/${billingAccountId}/payment-method`, {
    withCreds: true,
  });
};

export const createPreviewInvoice = async (
  billingAccountId: UserBillingAccount["id"],
  quantityDelta: number
) => {
  const invoice: ApiPreviewInvoice = await post(
    `/billing/${billingAccountId}/preview-invoice`,
    { quantityDelta },
    { withCreds: true }
  );

  return parsePreviewInvoice(invoice);
};

export const getBillingSubscription = async (
  billingAccountId: string
): Promise<BillingSubscription> => {
  const subscription = (await get(`/billing/${billingAccountId}/subscription`, {
    withCreds: true,
  })) as ApiBillingSubscription;

  return {
    ...subscription,
    status: subscription.stripeSubscriptionStatus,
  };
};

export const updateSubscription = async (
  billingAccountId: UserBillingAccount["id"],
  body: PatchSubscriptionRequestBody
) => {
  return patch(`/billing/${billingAccountId}/subscription`, body, {
    withCreds: true,
  });
};

export const cancelSubscription = async (billingAccountId: string) =>
  httpDelete(`/billing/${billingAccountId}/subscription`, { withCreds: true });

export const getBillingHistory = async (
  billingAccountId: UserBillingAccount["id"]
): Promise<Invoice[]> => {
  const invoiceResponse: { invoices: Invoice[] } = await get(
    `/billing/${billingAccountId}/invoices`,
    { withCreds: true }
  );
  return filterInvoices(invoiceResponse.invoices ?? []);
};

export const listPromotions = async (
  billingAccountId: UserBillingAccount["id"]
): Promise<Promotion[]> => {
  const response: ApiPromotion[] = await get(
    `/billing/${billingAccountId}/promotions`,
    { withCreds: true }
  );
  return response ?? [];
};

export const deletePromotion = async (
  billingAccountId: UserBillingAccount["id"],
  promotionId: Promotion["id"]
): Promise<void> => {
  return httpDelete(`/billing/${billingAccountId}/promotions/${promotionId}`, {
    withCreds: true,
  });
};
